import { useState, useRef } from 'react';
import api from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Upload, FileIcon, X, Download, Image, FileText, Loader2, Eye } from 'lucide-react';
import { toast } from 'sonner';
import FilePreviewDialog, { isPreviewable } from '@/components/FilePreviewDialog';

const FILE_ICONS = {
  'image/': Image,
  'application/pdf': FileText,
  'text/': FileText,
};

function getIcon(contentType) {
  for (const [prefix, Icon] of Object.entries(FILE_ICONS)) {
    if (contentType?.startsWith(prefix)) return Icon;
  }
  return FileIcon;
}

function formatSize(bytes) {
  if (!bytes) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export function FileUpload({ entityType, entityId, onUploadComplete }) {
  const [uploading, setUploading] = useState(false);
  const inputRef = useRef(null);

  const handleUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;
    setUploading(true);
    try {
      for (const file of files) {
        const formData = new FormData();
        formData.append('file', file);
        await api.post(`/files/upload?entity_type=${entityType}&entity_id=${entityId}`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
      }
      toast.success(`${files.length} fichier(s) ajouté(s)`);
      if (onUploadComplete) onUploadComplete();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur upload');
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = '';
    }
  };

  return (
    <div>
      <input ref={inputRef} type="file" multiple onChange={handleUpload} className="hidden" id={`upload-${entityType}-${entityId}`} />
      <Button
        type="button"
        variant="outline"
        size="sm"
        disabled={uploading}
        onClick={() => inputRef.current?.click()}
        className="border-zinc-700 text-zinc-400 hover:text-zinc-100 hover:bg-zinc-800 rounded-sm text-xs h-8"
        data-testid={`upload-btn-${entityType}`}
      >
        {uploading ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <Upload className="w-3.5 h-3.5 mr-1.5" />}
        {uploading ? 'Upload...' : 'Ajouter fichier'}
      </Button>
    </div>
  );
}

export function FileList({ files = [], onDelete, showDelete = true }) {
  const [downloading, setDownloading] = useState(null);
  const [previewFile, setPreviewFile] = useState(null);

  const handleDownload = async (file) => {
    setDownloading(file.id);
    try {
      const response = await api.get(`/files/download/${file.id}`, { responseType: 'blob' });
      const url = URL.createObjectURL(response.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = file.original_filename;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error('Erreur de téléchargement');
    } finally {
      setDownloading(null);
    }
  };

  const handleDelete = async (fileId) => {
    try {
      await api.delete(`/files/${fileId}`);
      toast.success('Fichier supprimé');
      if (onDelete) onDelete();
    } catch {
      toast.error('Erreur');
    }
  };

  if (files.length === 0) return null;

  return (
    <div className="space-y-1.5" data-testid="file-list">
      {files.map(file => {
        const Icon = getIcon(file.content_type);
        const CAT_LABELS = {
          facture: { label: 'Facture', cls: 'bg-emerald-500/10 text-emerald-400 border-emerald-500/30' },
          bon_livraison: { label: 'BL', cls: 'bg-sky-500/10 text-sky-400 border-sky-500/30' },
          certificat: { label: 'Certif.', cls: 'bg-violet-500/10 text-violet-400 border-violet-500/30' },
          rapport_controle: { label: 'Rapport', cls: 'bg-amber-500/10 text-amber-400 border-amber-500/30' },
          photo: { label: 'Photo', cls: 'bg-pink-500/10 text-pink-400 border-pink-500/30' },
        };
        const cat = CAT_LABELS[file.category];
        const fromSub = file.uploaded_by_role === 'subcontractor';
        const canPreview = isPreviewable(file);
        return (
          <div
            key={file.id}
            className={`flex items-center gap-2 bg-zinc-950 border rounded-sm px-3 py-2 group ${fromSub ? 'border-amber-500/30' : 'border-zinc-800'} ${canPreview ? 'cursor-pointer hover:border-amber-500/60' : ''}`}
            onClick={canPreview ? () => setPreviewFile(file) : undefined}
            data-testid={`file-${file.id}`}
          >
            <Icon className="w-4 h-4 text-zinc-500 flex-shrink-0" />
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-1.5">
                <p className="text-xs text-zinc-300 truncate">{file.original_filename}</p>
                {cat && (
                  <span className={`text-[9px] font-semibold uppercase tracking-wider px-1.5 py-0.5 rounded-sm border ${cat.cls} flex-shrink-0`}>
                    {cat.label}
                  </span>
                )}
                {fromSub && (
                  <span className="text-[9px] font-semibold uppercase tracking-wider px-1.5 py-0.5 rounded-sm border bg-amber-500/10 text-amber-400 border-amber-500/30 flex-shrink-0">
                    Sous-trait.
                  </span>
                )}
              </div>
              <p className="text-[10px] text-zinc-600">{formatSize(file.size)}{file.uploaded_by_name ? ` - ${file.uploaded_by_name}` : ''}</p>
            </div>
            <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity" onClick={(e) => e.stopPropagation()}>
              {canPreview && (
                <button
                  onClick={() => setPreviewFile(file)}
                  className="p-1 text-zinc-500 hover:text-amber-400 transition-colors"
                  data-testid={`preview-file-${file.id}`}
                  title="Aperçu"
                >
                  <Eye className="w-3.5 h-3.5" />
                </button>
              )}
              <button
                onClick={() => handleDownload(file)}
                disabled={downloading === file.id}
                className="p-1 text-zinc-500 hover:text-blue-400 transition-colors"
                data-testid={`download-file-${file.id}`}
                title="Télécharger"
              >
                {downloading === file.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Download className="w-3.5 h-3.5" />}
              </button>
              {showDelete && (
                <button onClick={() => handleDelete(file.id)} className="p-1 text-zinc-500 hover:text-rose-400 transition-colors" data-testid={`delete-file-${file.id}`}>
                  <X className="w-3.5 h-3.5" />
                </button>
              )}
            </div>
          </div>
        );
      })}
      <FilePreviewDialog file={previewFile} open={!!previewFile} onOpenChange={(v) => !v && setPreviewFile(null)} />
    </div>
  );
}
